/**
 * Contexto de autenticação: usuário da sessão, login, registro, logout e
 * atualização. Deve ficar dentro de `<BrowserRouter>` (usa `useNavigate`)
 * para poder reagir a uma sessão expirada sem recarregar a página.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, post, setUnauthorizedHandler } from '@/lib/api';
import type { LoginInput, RegisterInput, User } from '@/lib/types';

export interface AuthContextValue {
  /** `null` enquanto não há sessão (ou ainda não sabemos). */
  user: User | null;
  /** `true` só durante a checagem inicial de `/auth/me`. */
  loading: boolean;
  login: (input: LoginInput) => Promise<User>;
  register: (input: RegisterInput) => Promise<User>;
  logout: () => Promise<void>;
  /** Rebusca `/auth/me` e atualiza o contexto — usar após mudanças de sessão externas. */
  refresh: () => Promise<User | null>;
  /** Atualiza o usuário em memória a partir da resposta de um PATCH — sem nova requisição. */
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname + location.search);

  useEffect(() => {
    locationRef.current = location.pathname + location.search;
  }, [location]);

  // `/auth/me` aqui é uma checagem, não uma ação sobre sessão já autenticada: para quem
  // nunca fez login, 401 é o resultado normal — não deve disparar o redirecionamento
  // global de sessão expirada (senão qualquer visita anônima seria empurrada para
  // /entrar, atropelando até a página 404 pública).
  const refresh = useCallback(async (): Promise<User | null> => {
    try {
      const res = await get<{ data: User }>('/auth/me', { suppressUnauthorized: true });
      setUserState(res.data);
      return res.data;
    } catch {
      setUserState(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await get<{ data: User }>('/auth/me', { suppressUnauthorized: true });
        if (active) setUserState(res.data);
      } catch {
        if (active) setUserState(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Sessão expirada/ausente: limpa o contexto e manda para /entrar preservando o destino,
  // sem recarregar a página (navegação do React Router).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUserState(null);
      setLoading(false);
      if (locationRef.current !== '/entrar') {
        navigate('/entrar', { replace: true, state: { from: locationRef.current } });
      }
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate]);

  const login = useCallback(async (input: LoginInput): Promise<User> => {
    // 401 aqui é "senha errada", tratado inline pela tela de login — não sessão expirada.
    const res = await post<{ data: User }>('/auth/login', input, { suppressUnauthorized: true });
    setUserState(res.data);
    return res.data;
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<User> => {
    const res = await post<{ data: User }>('/auth/register', input, { suppressUnauthorized: true });
    setUserState(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await post('/auth/logout');
    } finally {
      setUserState(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, register, logout, refresh, setUser: setUserState }),
    [user, loading, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>.');
  return ctx;
}
